/**
 * v0.5① 日報機能 P-3 (§14-3-A「自動保存」): フィールド値・明細行(line_items)の単一直列送信キュー。
 *
 * ⚠️ design-expertレビューCRITICAL是正 (2026-09-09、設計書に確定済みの設計をそのまま実装する):
 * `row_version` は daily_reports (レポート全体で1本、§3-1) のため、フィールド単位で独立に
 * デバウンス送信すると、複数フィールドを続けて編集する通常操作で2つ目以降のPATCHが1つ目の
 * 応答前の古い row_version を持ったまま送信され、自分自身と409衝突する。
 * ⇒ 変更は一旦ローカルの保留キューに積み、500msデバウンスでまとめて1本のリクエストを送る。
 * in-flight は常に1本まで。line_items (PUT /:id/line-items/:field_definition_id) も別チャネル
 * だが同じ row_version 変数を参照・更新する (フィールド値の送信 → line_items の送信の順に
 * 直列で行い、都度 row_version を更新してから次を送る)。
 *
 * 409 (ROW_VERSION_MISMATCH) は「サーバーの最新値を再取得して自分の未送信キューとマージ」する
 * 非破壊的な設計 (強制上書きしない)。呼び出し元 (DailyReportDetailPage) が onConflict で
 * 再取得・表示更新を行い、このフックは失敗した送信内容をキューに戻して再送に備える。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import {
  patchDailyReportValues, putDailyReportLineItems,
  type DailyReportValuePatchEntry, type DailyReportLineItemPutRow,
} from '@/lib/daily-report-api';

export type DailyReportAutosaveStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

type LineItemsQueueEntry = { fieldDefinitionId: string; rows: DailyReportLineItemPutRow[] };

const formatHHmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export const useDailyReportAutosave = (dailyReportId: string, initialRowVersion: number) => {
  const rowVersionRef = useRef(initialRowVersion);
  const [rowVersion, setRowVersionState] = useState(initialRowVersion);
  const [status, setStatus] = useState<DailyReportAutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const fieldQueueRef = useRef<Map<string, DailyReportValuePatchEntry>>(new Map());
  const lineItemsQueueRef = useRef<Map<string, LineItemsQueueEntry>>(new Map());
  const inFlightRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onConflictRef = useRef<(() => Promise<void> | void) | null>(null);

  const setRowVersion = useCallback((v: number) => {
    rowVersionRef.current = v;
    setRowVersionState(v);
  }, []);

  const hasPending = () => fieldQueueRef.current.size > 0 || lineItemsQueueRef.current.size > 0;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define -- flush/scheduleFlush は相互再帰 (flush完了後に残キューがあれば即座に次を呼ぶ)
  const flush = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!hasPending()) return;
    if (!navigator.onLine) { setStatus('offline'); return; }

    const fieldEntries = [...fieldQueueRef.current.values()];
    fieldQueueRef.current.clear();
    const lineItemEntries = [...lineItemsQueueRef.current.values()];
    lineItemsQueueRef.current.clear();

    inFlightRef.current = true;
    setStatus('saving');
    try {
      let version = rowVersionRef.current;
      if (fieldEntries.length > 0) {
        const res = await patchDailyReportValues(dailyReportId, version, fieldEntries);
        version = res.row_version;
      }
      for (const li of lineItemEntries) {
        // eslint-disable-next-line no-await-in-loop -- line_itemsチャネルもrow_versionを共有するため直列必須
        const res = await putDailyReportLineItems(dailyReportId, li.fieldDefinitionId, version, li.rows);
        version = res.row_version;
      }
      setRowVersion(version);
      setStatus('saved');
      setLastSavedAt(formatHHmm(new Date()));
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'ROW_VERSION_MISMATCH') {
        // 失敗した送信内容をキューへ戻す (既に積まれている新しい変更があれば、Mapのキー上書きで
        // その新しい変更が優先される — フィールド単位の「最後に勝つ」意味論のまま整合する)。
        for (const e of fieldEntries) if (!fieldQueueRef.current.has(e.field_definition_id)) fieldQueueRef.current.set(e.field_definition_id, e);
        for (const e of lineItemEntries) if (!lineItemsQueueRef.current.has(e.fieldDefinitionId)) lineItemsQueueRef.current.set(e.fieldDefinitionId, e);
        setStatus('error');
        toast.info('他の変更と競合したため、最新の内容を再読み込みしました');
        await onConflictRef.current?.();
      } else if (!navigator.onLine) {
        for (const e of fieldEntries) if (!fieldQueueRef.current.has(e.field_definition_id)) fieldQueueRef.current.set(e.field_definition_id, e);
        for (const e of lineItemEntries) if (!lineItemsQueueRef.current.has(e.fieldDefinitionId)) lineItemsQueueRef.current.set(e.fieldDefinitionId, e);
        setStatus('offline');
      } else {
        setStatus('error');
        toast.error(err instanceof ApiClientError ? err.message : '自動保存に失敗しました');
      }
    } finally {
      inFlightRef.current = false;
      if (hasPending() && navigator.onLine) void flush();
    }
  }, [dailyReportId, setRowVersion]);

  const scheduleFlush = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => { void flush(); }, 500);
  }, [flush]);

  const queueValue = useCallback((entry: DailyReportValuePatchEntry) => {
    fieldQueueRef.current.set(entry.field_definition_id, entry);
    scheduleFlush();
  }, [scheduleFlush]);

  const queueLineItems = useCallback((fieldDefinitionId: string, rows: DailyReportLineItemPutRow[]) => {
    lineItemsQueueRef.current.set(fieldDefinitionId, { fieldDefinitionId, rows });
    scheduleFlush();
  }, [scheduleFlush]);

  const flushNow = useCallback(async () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    await flush();
  }, [flush]);

  const setOnConflict = useCallback((fn: (() => Promise<void> | void) | null) => { onConflictRef.current = fn; }, []);

  // オフライン→オンライン復帰時に自動で再開する (§14-3-A「再接続時は保留キューを1回の送信にまとめて再開」)。
  useEffect(() => {
    const onOnline = () => { if (hasPending()) void flush(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flush]);

  useEffect(() => () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); }, []);

  return {
    rowVersion, status, lastSavedAt,
    queueValue, queueLineItems, flushNow, setRowVersion, setOnConflict,
    isFieldQueued: (fieldDefinitionId: string) => fieldQueueRef.current.has(fieldDefinitionId),
    isLineItemsQueued: (fieldDefinitionId: string) => lineItemsQueueRef.current.has(fieldDefinitionId),
  };
};
